import "./Quiz.css";
import {
  faCircleQuestion,
  faExclamationTriangle,
  faInfoCircle,
  faPen,
  faPlus,
  faTrash,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useCallback, useEffect, useState } from "react";
import { Alert, Button, Col, Container, Modal, Row } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { Button as AntButton, Tag } from "antd";
import { TailSpin } from "react-loader-spinner";
import dayjs from "dayjs";
import { useToasts } from "../../contexts/toasts";
import { Quizzes } from "../../api/Quiz";
import { Quiz, QuizStatus } from "../../types/Quiz";

export const formatQuizDate = (value: string) =>
  dayjs(value).format("DD/MM/YYYY HH:mm");

export function QuizStatusTag({ status }: { status: QuizStatus }) {
  switch (status) {
    case "active":
      return <Tag color="green">In corso</Tag>;
    case "scheduled":
      return <Tag color="blue">Programmato</Tag>;
    case "ended":
      return <Tag color="default">Terminato</Tag>;
    default:
      return <Tag color="orange">Disattivato</Tag>;
  }
}

export default function QuizList() {
  const navigate = useNavigate();
  const { showToast } = useToasts();
  const [quizzes, setQuizzes] = useState<Quiz[]>();
  const [error, setError] = useState(false);
  const [quizToDelete, setQuizToDelete] = useState<Quiz>();
  const [deleteLoading, setDeleteLoading] = useState(false);

  const initQuizzes = useCallback(() => {
    setError(false);
    setQuizzes(undefined);
    Quizzes.getQuizzes().then(
      ({ data }) => setQuizzes(data.list),
      () => {
        setQuizzes([]);
        setError(true);
        showToast(
          "quizzes-retrieve-error",
          "Errore nel recupero dei quiz, per favore riprova.",
          "",
          "danger"
        );
      }
    );
  }, [showToast]);

  useEffect(() => {
    initQuizzes();
  }, [initQuizzes]);

  const handleClose = useCallback(() => setQuizToDelete(undefined), []);

  const deleteQuiz = useCallback(() => {
    if (!quizToDelete) return;
    setDeleteLoading(true);
    Quizzes.deleteQuiz(quizToDelete.id).then(
      () => {
        showToast("delete-quiz-success", "Quiz eliminato", "", "success");
        setDeleteLoading(false);
        handleClose();
        initQuizzes();
      },
      () => {
        showToast(
          "delete-quiz-error",
          "Errore nella cancellazione del quiz",
          "",
          "danger"
        );
        setDeleteLoading(false);
      }
    );
  }, [quizToDelete, showToast, handleClose, initQuizzes]);

  const renderContent = () => {
    if (!quizzes)
      return (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        </div>
      );

    if (error)
      return (
        <Alert variant="danger" className="py-2">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span className="ms-2">
            C'è stato un errore, per favore riprova più tardi
          </span>
        </Alert>
      );

    if (quizzes.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">
            Nessun quiz creato. Crea il primo con "Nuovo Quiz".
          </span>
        </Alert>
      );

    return (
      <Row className="g-3">
        {quizzes.map((quiz) => (
          <Col xs={12} sm={6} lg={4} key={quiz.id}>
            <div
              className={`quiz-card quiz-card-${quiz.status}`}
              onClick={() => navigate(`/quiz/${quiz.id}`)}
              role="button"
            >
              <div className="quiz-card-top">
                <div className="quiz-card-title">
                  <FontAwesomeIcon icon={faCircleQuestion} className="me-2" />
                  {quiz.title}
                </div>
                <QuizStatusTag status={quiz.status} />
              </div>
              <div className="quiz-card-dates">
                Dal {formatQuizDate(quiz.startDate)}
                <br />
                al {formatQuizDate(quiz.endDate)}
              </div>
              <div className="quiz-card-stats">
                <span>
                  <strong>{quiz.questions.length}</strong>{" "}
                  {quiz.questions.length === 1 ? "domanda" : "domande"}
                </span>
                <span>
                  <strong>{quiz.maxPoints}</strong> punti in palio
                </span>
                <span>
                  <FontAwesomeIcon icon={faUsers} className="me-1" />
                  <strong>{quiz.submissions}</strong>{" "}
                  {quiz.submissions === 1 ? "partecipante" : "partecipanti"}
                </span>
              </div>
              <div className="quiz-card-footer">
                <AntButton
                  size="small"
                  icon={<FontAwesomeIcon icon={faPen} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/quiz/${quiz.id}/modifica`);
                  }}
                >
                  Modifica
                </AntButton>
                <AntButton
                  size="small"
                  danger
                  icon={<FontAwesomeIcon icon={faTrash} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setQuizToDelete(quiz);
                  }}
                >
                  Elimina
                </AntButton>
              </div>
            </div>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <Container fluid="md" className="mt-5">
      <Row className="mb-2 align-items-center">
        <Col>
          <h4 className="mb-0">Quiz</h4>
        </Col>
        <Col xs={6} md={4} className="text-end">
          <button
            onClick={() => navigate("/quiz/nuovo")}
            className="button-primary"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span className="d-none d-sm-inline ms-2">Nuovo Quiz</span>
          </button>
        </Col>
      </Row>
      <hr />
      <p className="text-muted">
        I quiz a tema sportivo compaiono nell'app a tutti gli utenti per il
        periodo indicato. Chi risponde correttamente guadagna i Get Move Points
        associati a ogni domanda.
      </p>

      {renderContent()}

      <Modal show={!!quizToDelete} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina quiz</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Vuoi eliminare il quiz <strong>{quizToDelete?.title}</strong>?
          {!!quizToDelete?.submissions && (
            <div className="text-muted mt-2">
              Verranno cancellati anche i risultati di{" "}
              {quizToDelete.submissions}{" "}
              {quizToDelete.submissions === 1
                ? "partecipante"
                : "partecipanti"}
              . I Get Move Points già assegnati restano agli utenti.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={handleClose}
          >
            Annulla
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={deleteQuiz}
            disabled={deleteLoading}
          >
            {deleteLoading ? (
              <TailSpin width={20} height={20} />
            ) : (
              <>
                <FontAwesomeIcon icon={faTrash} />
                <span> Elimina</span>
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
